// OrderDetails.jsx
import React from 'react';
import './OrderDetails.css';

// Stored line items carry no product name, so custom ads are recognized by
// the fields only Build Your Ad fills in.
const isCustomAdLineItem = (lineItem) =>
  !!(lineItem.adType || lineItem.adSize || lineItem.numVehicles || lineItem.offerType || lineItem.notes);

const OrderDetails = ({ order, productDetails, loading }) => {
  // Helper to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getItemName = (lineItem) => {
    if (lineItem && lineItem.name) {
      return lineItem.name;
    } else if (lineItem && lineItem.productDetails && lineItem.productDetails.name) {
      return lineItem.productDetails.name;
    } else if (productDetails && productDetails.name) {
      return productDetails.name;
    }
    return 'Business Cards';
  };

  // Format the date time with timezone
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    const options = {
      weekday: 'short',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    };

    return date.toLocaleDateString('en-US', options);
  };

  // Show loading state for product details
  if (loading) {
    return (
      <div className="order-details">
        <div className="loading-details">Loading product details...</div>
      </div>
    );
  }

  // Check if line items exist
  const hasLineItems = order.lineItems && order.lineItems.length > 0;
  const hasCustomItems = (order.lineItems || []).some(isCustomAdLineItem);
  const orderTotal = Number(order.orderTotal) || 0;

  return (
    <div className="order-details">
      <h2>CADILLAC AD BUILDER</h2>

      <div className="details-row">
        <span className="details-label">DATE</span>
        <span className="details-value">{formatDateTime(order.createdAt)}</span>
      </div>

      <div className="details-row">
        <span className="details-label">{order.name || 'Dealership'}</span>
        <span className="details-value"> (BAC: {order.bac || 'N/A'})</span>
      </div>

      <div className="details-row">
        <span className="details-label">Email:</span>
        <span className="details-value">{order.email || 'N/A'}</span>
      </div>

      <div className="details-row">
        <span className="details-label">Phone #</span>
        <span className="details-value">{order.phone || 'N/A'}</span>
      </div>

      <div className="details-row">
        <span className="details-label">Order #</span>
        <span className="details-value">{order.id}</span>
      </div>

      <div className="details-row">
        <span className="details-label">Order Notes</span>
        <span className="details-value">{order.message}</span>
      </div>

      {/* Display line items if they exist */}
      {hasLineItems ? (
        order.lineItems.map((lineItem, index) => {
          const isCustomAd = isCustomAdLineItem(lineItem);

          return (
            <div key={index} className="product-details">
              <div className="product-name">{getItemName(lineItem)}</div>

              {isCustomAd ? (
                <div className="custom-ad-details">
                  {lineItem.adType && (
                    <div className="custom-ad-option">
                      <span className="option-label">Ad Type:</span>
                      <span className="option-value">{lineItem.adType}</span>
                    </div>
                  )}
                  {lineItem.adSize && (
                    <div className="custom-ad-option">
                      <span className="option-label">Size:</span>
                      <span className="option-value">{lineItem.adSize}</span>
                    </div>
                  )}
                  {lineItem.numVehicles && (
                    <div className="custom-ad-option">
                      <span className="option-label">Vehicles:</span>
                      <span className="option-value">{lineItem.numVehicles}</span>
                    </div>
                  )}
                  {lineItem.offerType && (() => {
                    let parsed = null;
                    try { parsed = JSON.parse(lineItem.offerType); } catch { /* legacy plain string */ }
                    return Array.isArray(parsed) ? (
                      <div className="custom-ad-option">
                        <span className="option-label">Offer Types:</span>
                        <span className="option-value">
                          {parsed.map(({ vehicle, offer }) => (
                            <div key={vehicle}>Vehicle {vehicle}: {offer}</div>
                          ))}
                        </span>
                      </div>
                    ) : (
                      <div className="custom-ad-option">
                        <span className="option-label">Offer Type:</span>
                        <span className="option-value">{lineItem.offerType}</span>
                      </div>
                    );
                  })()}
                  {lineItem.notes && (
                    <div className="custom-ad-option">
                      <span className="option-label">Notes:</span>
                      <span className="option-value">{lineItem.notes}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="product-specs">
                  <div className="spec-row">Package: Quantity of {lineItem.quantity || 0}</div>
                  <div className="spec-row">Creative: Preferred Option (Cadillac Crest)</div>
                  {/*  Optional Employee Name */}
                  {lineItem.employee && (
                    <div className="spec-row">
                      Assigned To: {lineItem.employee.name}
                    </div>
                  )}
                  <div className="spec-row">Price: {formatCurrency(lineItem.price || 0)}</div>
                  <div className="spec-row">
                    GM Billing Fee ({(lineItem.fee || 0)}%): {formatCurrency((lineItem.price || 0) * (lineItem.fee / 100 || 0))}
                  </div>
                  <div className="spec-row">
                    Subtotal: {formatCurrency(
                      ((Number(lineItem.price) || 0) + (Number(lineItem.price) || 0) * (Number(lineItem.fee / 100) || 0))
                    )}
                  </div>
                </div>
              )}

            </div>
          );
        })
      ) : (
        <div className="product-details">
          <div className="product-name">Order Details</div>
          <div className="product-specs">
            <div className="spec-row">No line items available for this order</div>
          </div>
        </div>
      )}

      <div className="details-row total">
        <span className="details-label">IMR Total</span>
        <span className="details-value">
          {/* Custom Creative work is quoted by MA — the stored total stays 0 */}
          {hasCustomItems && orderTotal === 0 ? 'Prices Vary' : formatCurrency(orderTotal)}
        </span>
      </div>

      {hasCustomItems && orderTotal > 0 && (
        <p className="custom-pricing-note">
          Total excludes Custom Creative items — MA will contact you with pricing.
        </p>
      )}
    </div>
  );
};

export default OrderDetails;
